import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApiQueryName, ApiVariables, ApiRequestOptions } from '../../../api/apiSchema';
import { AsyncResult, Result } from '../../../core';
import { BindingProps, StoreNode } from '../../../store';
import { ChartPageParams } from '../chartSchema';
import { Store } from '../../../store/store';
import { error, Error } from '../../../core/error';
import { ApiError } from '../../../api/apiError';
import { IMetricCounterDataSource } from '../metricCounterSchema';

type ValueAccessor = (apiResult: unknown) => [number | null, (Error | string)?];

type Props = BindingProps<{
  queryName?: ApiQueryName;
  queryParams?: ChartPageParams;
  valueAccessor?: ValueAccessor;
}>

export class MetricCounterDataSource
  extends StoreNode
  implements IMetricCounterDataSource<number> {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable<this, 'insertResponse' | 'handleError'>(this, {
      queryName: computed,
      queryParams: computed,
      valueAccessor: computed,
      value: observable,
      isFetching: observable,
      error: observable,
      reset: action,
      fetch: action,
      insertResponse: action,
      handleError: action,
    });
  }

  // #region Props
  get queryName(): ApiQueryName | null {
    return this.getResolvedProp('queryName') ?? null;
  }

  get queryParams(): ChartPageParams | null {
    return this.getResolvedProp('queryParams') ?? null;
  }

  get valueAccessor(): ValueAccessor | null {
    return this.getProp('valueAccessor') ?? null;
  }
  // #endregion

  value = 0;

  private abortController: AbortController | null = null;

  isFetching = false;
  error: Error | null = null;

  reset() {
    this.abort();

    this.isFetching = false;
    this.error = null;
    this.abortController = null;

    this.value = 0;
  }

  async fetch(): AsyncResult {

    this.reset();

    const abortCtrl = new AbortController();
    this.abortController = abortCtrl;

    const { queryName, queryParams } = this;
    if (!(queryName && queryParams))
      return [null];

    this.isFetching = true;

    const startDateArg = queryParams.startDate?.toISO();
    const endDateArg = queryParams.endDate?.toISO();
    const timezoneArg = queryParams.timezone ?? undefined;
    const teamIdArg = queryParams.teamId ?? undefined;

    if (!startDateArg || !endDateArg)
      return [null, error('Unknown', 'Invalid parameters')];

    const reqArgs: ApiVariables<'keenBatch'> = {
      args: {
        start: startDateArg,
        end: endDateArg,
        timezone: timezoneArg,
        teamId: teamIdArg
      }
    };

    const api = this.store.api;
    const reqOpts: ApiRequestOptions = {
      signal: abortCtrl.signal
    };

    const [res, err] = await api.runQuery(queryName, reqArgs, reqOpts);

    if (err) {
      return this.handleError(err);
    }

    const [value, insertErr] = this.insertResponse(res!);
    if (insertErr)
      return this.handleError(insertErr);

    runInAction(() => {
      this.isFetching = false;
    });

    return [value!];
  }

  cancel(): void {
    this.abort();
  }

  private insertResponse(res: unknown): Result<number> {
    const [result, parseErr] = (this.valueAccessor && this.valueAccessor(res)) || [null];

    if (parseErr)
      return this.handleError(parseErr);

    this.value = result ?? 0;
    return [this.value];
  }

  private abort() {

    const lastAbortCtrl = this.abortController;
    if (lastAbortCtrl)
      lastAbortCtrl.abort();
    this.abortController = null;
  }

  private handleError(err?: Error | string): Result {
    if (!err)
      err = new Error();
    if (typeof err === 'string')
      err = new Error('Unknown', err);

    if (err instanceof ApiError && err.type === 'RequestAborted') {
      // for aborted requests we only return the error but we don't set it on the instance and we do not repor it as error
      return [null, err];

    } else {
      console.error(`An error occured in MetricCounterDataSource: `, err);

      this.isFetching = false;
      this.abort();
      this.error = err;

      return [null, err];
    }
  }
}
